import {
	AlertBar,
	computeContextMenuTitle,
	ContextMenu,
	Dialog,
	Pagination,
	SearchInputForm,
	TableView,
	TableViewNoResultConfig,
	Toolbar,
	useContextMenu
} from "client/Components";
import { contains } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	getOffice365Groups,
	importFromOffice365Groups,
	Office365GroupModel
} from "client/Api/ImportUsers/importUsers";
import {
	Action,
	ActionKind,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import styles from "./ImportUserGroupPage.module.less";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { userManagementRouting } from "./UserManagementPage";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type ImportUserGroupPageProps = {
	onNavigate: NavigationCallback;
};

export const ImportUserGroupPage = (
	props: ImportUserGroupPageProps
): ReactElement => {
	const {
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isAlertDismissed, setIsAlertDismissed] = useState<boolean>(false);
	const [importErrors, setImportErrors] = useState<readonly UserImportError[] | undefined>();
	const [items, setItems] = useState<readonly Office365GroupModel[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const [sortingState, setSortingState] = useState<DataSourceSortableState<Office365GroupModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);
	const [selectedItems, setSelectedItems] = useState<readonly Office365GroupModel[]>([]);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	useActiveSettingsMenuItem(SettingsPageKind.UserManagement);

	const { sortingFor } = useSortHandler<Office365GroupModel>(sortingState, setSortingState);

	const { currentItems, pagination } = useOffice365GroupDataSource(
		items,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber,
				amountPerPage: 100
			},
			searchTerm
		}
	);

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					const groups: readonly Office365GroupModel[] = await getOffice365Groups(signal)
						.catch(handlePageDataLoadingError);

					setItems(groups);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(currentPageNumber);
			hasImplicitDependencyOn(sortingState);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			sortingState,
			searchTerm
		]
	);

	const importHandler = useCallback(
		async (selection: readonly Office365GroupModel[]): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const importResult = await importFromOffice365Groups(
					selection.map(x => x.id),
					globalCancellation
				);

				if (importResult.failedUsers.length === 0) {
					alertManager.showSuccess(localize("_TheUsersFromSelectedGroupsAreSuccessfullyImported"));
				} else {
					const importErrors: readonly UserImportError[] = importResult.failedUsers
						.map(it => ({
							user: it.email,
							reason: it.importError
								.errors
								.map(error => error.propertyName ? `${error.propertyName}: ${error.errorMessage}` : error.errorMessage)
								.join(", ")
						}));

					setImportErrors(importErrors);
				}

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_ErrorDuringImport"));
				setIsBusy(false);
			}
		},
		[
			isBusy,
			alertManager,
			globalCancellation
		]
	);

	const backHandler = useCallback(
		(): void => {
			onNavigate(userManagementRouting.overview);
		},
		[onNavigate]
	);

	const actionHandlers = useMemo(
		(): ContextualActionsHandlers => ({
			import: importHandler
		}),
		[importHandler]
	);

	const contextualActions = useMemo(
		(): readonly ImportUserGroupAction[] => {
			return listContextualActions(actionHandlers);
		},
		[actionHandlers]
	);

	const actionContext = useMemo(
		(): ImportUserGroupActionContext => ({
			selection: selectedItems
		}),
		[selectedItems]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ImportUserGroupAction[] => {
			return buildActionsForTopBar<ImportUserGroupActionContext, ImportUserGroupAction>(
				{
					contextualActions,
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			contextualActions,
			isResponsive,
			hasSelection
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "user-group",
				title: localize("_NoGroups"),
				message: localize("_NoGroupsToShowYet")
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				hasSelection={hasSelection}
				title={localize("_ImportFromOffice365Groups")}
				onBack={backHandler}
			>
				{!isAlertDismissed && (
					<div className={styles.alertBarContainer}>
						<AlertBar
							kind="info"
							isInline
							onDismiss={() => setIsAlertDismissed(true)}
						>
							{localize("_DistributionAndMailEnabledGroupsNotice")}
						</AlertBar>
					</div>
				)}

				<SettingsPage.Section height="stretch">
					<SettingsPage.SearchSection>
						<SearchInputForm onChange={setSearchTerm} />
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							trackingId="import-user-group-grid"
							items={currentItems}
							itemKey="id"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									width: "fill",
									trackingId: "name"
								}
							]}
							ref={tableRef}
							height="stretch"
							noResultConfig={noResultConfig}
						>
							{item => (
								<TableView.Row
									selectable={selectionFor(item)}
									trackingId={item.id}
									onContextMenu={contextMenuHandler}
								>
									<TableView.Cell>{item.name}</TableView.Cell>
								</TableView.Row>
							)}
						</TableView>
						<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
						{contextMenuProps && (
							<ContextMenu
								{...contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="import-user-group-context-menu"
								title={computeContextMenuTitle(
									actionContext.selection,
									(x) => x.name
								)}
							/>
						)}
					</SettingsPage.Section>
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>

			{importErrors && (
				<NotAllItemsWereImportedDialog
					errors={importErrors}
					onClose={() => setImportErrors(undefined)}
				/>
			)}
		</>
	);
};
ImportUserGroupPage.displayName = "ImportUserGroupPage";

export const ImportUserGroupPageForRouter: FunctionComponent = connectToRouter(
	ImportUserGroupPage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

function searchMatchFunction(
	item: Office365GroupModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name
	} = item;

	return contains(name, normalizedNotEmptySearchTerm);
}

type ImportUserGroupActionContext = { selection: readonly Office365GroupModel[]; };
type ImportUserGroupAction = Action<ImportUserGroupActionContext>;

type ContextualActionsHandlers = {
	import: (selection: readonly Office365GroupModel[]) => void;
};

function listContextualActions(handlers: ContextualActionsHandlers): readonly ImportUserGroupAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "import",
			title: localize("_Import"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => handlers.import(c.selection),
			icon: "cloud-arrow-down"
		}
	];
}

const useOffice365GroupDataSource = createLocalDataSource<Office365GroupModel>()
	.setConfiguration({
		search: { doesMatchItem: searchMatchFunction },
		hasPagination: true
	})
	.getHook();

type UserImportError = {
	user: string;
	reason: string;
};

export type NotAllItemsWereImportedDialogProps = {
	errors: readonly UserImportError[];
	onClose: () => void;
};

export const NotAllItemsWereImportedDialog = (props: NotAllItemsWereImportedDialogProps): ReactElement => {
	const {
		errors,
		onClose
	} = props;

	return (
		<Dialog trackingId="import-office365-groups-error-dialog">
			<Dialog.Header title={localize("_NotAllUsersWereImported")} onClose={onClose} />
			<Dialog.Body>
				<p className="dialog-message">
					{localize("_TheFollowingUsersCouldNotBeImported")}
				</p>
				<TableView
					trackingId="import-error-grid"
					items={errors}
					itemKey={indexBasedKey}
					hasZebraPrinting
					columns={[
						{
							title: localize("_Username"),
							width: 250,
							trackingId: "username"
						},
						{
							title: localize("_Reason"),
							trackingId: "reason"
						}
					]}
				>
					{(x) => (
						<TableView.Row
							trackingId={x.user}
						>
							<TableView.Cell>{x.user}</TableView.Cell>
							<TableView.Cell>{x.reason}</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="close"
						text={localize("_Close")}
						onClick={onClose}
						kind="primary"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
NotAllItemsWereImportedDialog.displayName = "NotAllItemsWereImportedDialog";

function indexBasedKey(_: unknown, index: number): string {
	return index.toString();
}